// De enige plek waar dit dashboard het netwerk aanraakt.
//
// Alles loopt over hetzelfde adres (/api): in productie zet de reverse proxy op
// de Hetzner-box de X-API-Key erop, lokaal doet de dev-proxy dat. De browser
// kent dus geen sleutel — hostingbesluit H1/H2 van W1c.
//
// Twee regels die uit W8 komen:
//  1. Een fout is nooit een witte pagina. Elke aanroep kan falen met een
//     ApiError die een LEESBARE reden draagt; het scherm toont die reden.
//  2. We verzinnen niets. Ontbreekt een optioneel veld, dan is het afwezig en
//     zegt het scherm waarom — niet een leeg vakje, niet een nul.

import type { EigenPijnAntwoord, GedeeldRequest, PlanResponse, SkillcijfersAntwoord, StandRequest, StandResponse } from "../nieuw/plan/contract";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";
import { bekijkAlsKoppen } from "./bekijkAls";
import type {
  ArbeidsmarktOverzicht,
  ArbeidsmarktReeksResponse,
  ChatOpeningsvragen,
  ChatProposities,
  ChatRequest,
  ChatResponse,
  ChatStap,
  BesluitRequest,
  BronBesluitRequest,
  BronBesluitResponse,
  BronGevolgResponse,
  BronLijstenResponse,
  BronnenResponse,
  ConfiguratieResponse,
  DekkingResponse,
  DekkingVerzoekRequest,
  DekkingVerzoekResponse,
  DuimRequest,
  DuimResponse,
  EngagementResponse,
  KredietRequest,
  KredietResponse,
  FeedResponse,
  FundingBesluitRequest,
  FundingControle,
  FundingControlesResponse,
  FundingDetailResponse,
  FundingListResponse,
  FundingVondst,
  FundingVondstBesluitRequest,
  FundingVondstenResponse,
  AfvinkRequest,
  ToegangRequest,
  ToegangResponse,
  AfvinkResponse,
  GebruikRequest,
  Gebruiker,
  GebruikersResponse,
  GesprekDetail,
  GesprekkenResponse,
  GezondheidResponse,
  GezondheidsbordResponse,
  InboxResponse,
  Instelling,
  InstellingRequest,
  KostenRamingRequest,
  KostenRamingResponse,
  KetenbalansResponse,
  KetenbalansSchakelaarRequest,
  KetenbalansSchakelaarResponse,
  KetenbalansUitzonderingRequest,
  KetenbalansUitzonderingResponse,
  KostenResponse,
  KwaliteitResponse,
  MeterkastIjkpuntRequest,
  MeterkastIjkpuntResponse,
  MeterkastOordeelRequest,
  MeterkastOordeelResponse,
  MeterkastResponse,
  MeterkastVergelijk,
  MetaResponse,
  PlanDelenResponse,
  PlanInstelling,
  PlanInstellingenResponse,
  PlanStartRequest,
  PlanVersieAntwoord,
  PlanVersieMeta,
  PlannenResponse,
  PlannenWissenResponse,
  SteekproefOordeelRequest,
  SteekproefOordeelResponse,
  SteekproefResponse,
  ValidatieAntwoordRequest,
  ValidatieAntwoordResponse,
  ValidatieLijstResponse,
  ValidatieOverzichtResponse,
  ValidatiePagina,
  ValidatieVastRequest,
  ValidatieVastResponse,
  Mij,
  OverzichtResponse,
  UitnodigingRequest,
  Verzoek,
  VerzoekRequest,
  WachtersResponse,
  WachtrijResponse,
  ObservatieDetailResponse,
  ObservatiesListResponse,
  PostenResponse,
  SampleResponse,
  SkillChartsResponse,
  SkillDetailResponse,
  SkillsListResponse,
  SourceDetailResponse,
  ConclusiesResponse,
  SourcesListResponse,
  TopicvoorstellenResponse,
  TrendDetailResponse,
  TrendsDeltaResponse,
  TrendsListResponse,
  GraafBuurtResponse,
  GraafKaartResponse,
  GraafVerbandResponse,
  GraafAlineaResponse,
  GraafBronnenResponse,
  GraafZoekResponse,
  GraafRapportenResponse,
  GraafSkillkaartResponse,
} from "../contracts";
import type {
  BeroepenRequest,
  BeroepenResponse,
  SectorBeroepen,
} from "../contracts/api/beheer";

export class ApiError extends Error {
  readonly status: number;
  readonly path: string;

  constructor(status: number, path: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.path = path;
  }

  /** Wat het scherm de gebruiker vertelt — nooit een technische dump. */
  get reason(): string {
    if (this.status === 404) return "Niet gevonden.";
    if (this.status === 401 || this.status === 403) {
      return "Geen toegang — opnieuw inloggen.";
    }
    if (this.status === 503) return "De dienst is even niet beschikbaar.";
    if (this.status === 0) return "Geen verbinding met de server.";
    return this.message || "Er ging iets mis bij het ophalen.";
  }
}

type QueryValue = string | number | boolean | undefined | null;
type Query = Record<QueryValue extends never ? never : string, QueryValue | readonly string[]>;

function queryString(params?: Query): string {
  if (!params) return "";
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    // Meervoudige assen reizen als herhaalde parameter (?category=a&category=b),
    // precies zoals FastAPI ze leest.
    if (Array.isArray(value)) {
      for (const v of value) if (v !== "") usp.append(key, String(v));
      continue;
    }
    usp.append(key, String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

/**
 * Wie het toegangstoken levert. Standaard: niemand — dan reist er geen
 * `Authorization` mee en draait dit dashboard tegen een dienst in
 * ontwikkelmodus. `lib/auth.ts` zet hier de Supabase-sessie in zodra die
 * bestaat (W1c-besluit H2).
 *
 * De omkering is met opzet: dit bestand kent Supabase niet. Zo blijft het de
 * enige plek die het netwerk aanraakt zonder ook de enige plek te worden die
 * de inlog kent — en zo hoeft een test geen inlog na te bootsen.
 */
let tokenBron: () => Promise<string | null> = async () => null;

export function zetTokenBron(bron: () => Promise<string | null>): void {
  tokenBron = bron;
}

/** `{Authorization: Bearer …}`, of niets als er geen sessie is. */
async function authKop(): Promise<Record<string, string>> {
  const token = await tokenBron();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Wie vraagt het: de inlog en de stand van "Bekijk als" (baan 2B). Voor de
 *  eigen fetch-wrappers (lib/funding.ts, lib/redactie.ts): zonder deze koppen
 *  krijgt beheer daar de stand van een niet-beheerder (geen interne sectoren,
 *  dus geen ICT-editie) en geldt "Bekijk als" er niet. */
export async function wieKoppen(): Promise<Record<string, string>> {
  return { ...(await authKop()), ...bekijkAlsKoppen() };
}

/** Met de trendlaag aan leest de service ook de trendlaag (sof/trendlaag/stand.py). */
function trendlaagKop(): Record<string, string> {
  return trendlaagAan() ? { "X-Sof-Trendlaag": "1" } : {};
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: globalThis.Response;
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(await authKop()),
        ...trendlaagKop(),
        // "Bekijk als" (baan 2B): alleen beheer; de dienst negeert het bij anderen.
        ...bekijkAlsKoppen(),
        ...init?.headers,
      },
    });
  } catch (err) {
    throw new ApiError(0, path, err instanceof Error ? err.message : "netwerkfout");
  }
  if (!res.ok) {
    // FastAPI stuurt {detail: "..."} — dat is een Nederlandse reden, dus bruikbaar.
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { detail?: unknown };
      if (typeof body.detail === "string") detail = body.detail;
      // Review ronde 2: bij een 422 stuurt FastAPI's eigen validatie een LIJST
      // (veld, regel, Engelse zin) in plaats van een Nederlandse reden. Die
      // lijst tonen we niet, en "422 Unprocessable Entity" ook niet: de
      // gebruiker hoort alleen dat de server de invoer niet aannam.
      else if (res.status === 422 && Array.isArray(body.detail)) {
        detail = "De server kon deze invoer niet aannemen — controleer de velden.";
      }
    } catch {
      // geen json-body; de statusregel is dan het beste wat we hebben
    }
    throw new ApiError(res.status, path, detail);
  }
  // 204 draagt per definitie geen body (POST /api/gebruik, W6). `res.json()`
  // zou daar een parse-fout gooien en een geslaagde aanroep als mislukt tonen.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function get<T>(path: string, params?: Query, signal?: AbortSignal): Promise<T> {
  return request<T>(`${path}${queryString(params)}`, signal ? { signal } : undefined);
}

// ── Filters per lijst, precies de parameters die de leeslaag kent ───────────

/** De filters van de chatlogs (GET /api/beheer/gesprekken, W6 ronde 5). `fout`
 *  reist alleen mee als hij aan staat: "fout=false" zou de server lezen als
 *  "zonder filter", maar een lege parameter is eerlijker dan een ontkenning. */
export type GesprekkenParams = { duim?: 1 | -1; gebruiker?: string; fout?: true };

export type TrendListParams = {
  page?: number;
  pageSize?: number;
  sector?: readonly string[];
  /** drager | context — de laag-as (W15): draagt de trend zelf, of onderbouwt hij? */
  laag?: string;
  /** vakgebied | arbeidsmarktobservatie — de soort-as (W8 scherm 9/14) */
  kind?: string;
  /** Macro | National | Sector — meervoudig (V1 lvlOpts) */
  level?: readonly string[];
  /** sterk | gemiddeld | zwak — band op de trending-score, meervoudig */
  signaal?: readonly string[];
  /** Categorieën uit de 25-waardige as (config/taxonomy/competentnl.yaml). */
  category?: readonly string[];
  /** super = alleen trends die andere trends bundelen (grofste detailniveau). */
  detail?: "super" | "kern" | "alles";
  /** uitgelicht (standaard) | trending | impact | urgentie | bronnen | nieuwste */
  sorteer?: string;
  /** Onderwerp-tags (config/taxonomy/topics.yaml). Meervoudig; een rij die één
   *  van de gekozen tags draagt telt mee — dus 'alle regelgeving' is de zeven
   *  regelgevingstags samen. */
  topic?: readonly string[];
  search?: string;
};

export type SkillListParams = {
  page?: number;
  pageSize?: number;
  /** Alle assen zijn meervoudig, net als bij trends. */
  sector?: readonly string[];
  type?: readonly string[];
  status?: readonly string[];
  category?: readonly string[];
  horizon?: readonly string[];
  /** zeer_hoog | hoog | midden | laag — band op de impact-score */
  impact?: readonly string[];
  /** zeer_laag | laag | midden | hoog — band op de effort-score */
  effort?: readonly string[];
  quickWin?: boolean;
  /** prioriteit | trending | urgentie (standaard prioriteit) */
  sorteer?: string;
  /** super = alleen skills die andere skills bundelen */
  detail?: "super" | "kern" | "alles";
  /** Onderwerp-tags (config/taxonomy/topics.yaml). Meervoudig; een rij die één
   *  van de gekozen tags draagt telt mee — dus 'alle regelgeving' is de zeven
   *  regelgevingstags samen. */
  topic?: readonly string[];
  search?: string;
  /** false = zonder de menutellingen (`facets`); de lijst zelf gebruikt ze niet. */
  facetten?: boolean;
  /** false = ook zonder koptellingen (total/totalGroups/totalMatching): voor een volgende pagina. */
  tellingen?: boolean;
};

export type SourceListParams = {
  page?: number;
  pageSize?: number;
  /**
   * De veertien assen uit W8 §4. De vijftiende die de owner goedkeurde,
   * "handmatig toegevoegd", bestaat niet als data — geen enkel veld legt vast
   * of een bron met de hand is toegevoegd, dus het scherm toont die reden in
   * plaats van een filter dat altijd hetzelfde antwoord geeft.
   */
  uitgever?: readonly string[];
  sector?: readonly string[];
  jaar?: readonly string[];
  niveau?: readonly string[];
  leesrol?: readonly string[];
  blikrichting?: readonly string[];
  bestandssoort?: readonly string[];
  /** ja | nee — draagt deze bron een bewijsregel onder een trend? */
  bewijs?: readonly string[];
  /** ja | nee — leverde deze bron skills op? */
  skills?: readonly string[];
  /** sterk | gemiddeld | zwak, op alle vijf de rubriekcijfers */
  gezag?: readonly string[];
  sectorrelevantie?: readonly string[];
  trendsignaal?: readonly string[];
  mbosignaal?: readonly string[];
  totaalscore?: readonly string[];
  /**
   * vak | arbeidsmarkt (A5, 2026-11-01). Zonder keuze: alleen vakbronnen. Met
   * "arbeidsmarkt" komen ook de stukken die alleen arbeidsmarkt-uitspraken dragen.
   */
  soort?: readonly string[];
  /** gezag | datum | uitgever (standaard gezag) */
  /** gezag | datum | uitgever | rel-b (meest gebruikt) | rel-a (soort bron) | rel-c (totaalscore) */
  sorteer?: string;
  search?: string;
  /**
   * false = alleen de rijen en het totaal, zonder facetten en kpis (A2,
   * 2026-11-01). De tellingen kosten meer dan de rijen en gelden per
   * filterkeuze, niet per pagina of sortering.
   */
  tellingen?: boolean;
};

export type ObservatieListParams = {
  page?: number;
  pageSize?: number;
  /** regio | brabant | nederland | internationaal | elders — de gebieden-as. */
  gebiedsniveau?: readonly string[];
  thema?: readonly string[];
  sector?: readonly string[];
  richting?: readonly string[];
  /** De eigen schaal van de uitspraak (reikwijdte_geo) — andere as dan gebied. */
  schaal?: readonly string[];
  onderbouwing?: readonly string[];
  indicator?: readonly string[];
  zoek?: string;
  /** A6: de pilotpropositie (key of school); zonder = alles zoals het was. */
  propositie?: string;
};

export const api = {
  /** `sector` (ADR 2026-10-23-ict-los-platform): tellingen en categorieën van
   *  die sector; zonder = alle zichtbare sectoren samen. */
  meta: (sector?: string | null) => get<MetaResponse>("/meta", sector ? { sector } : undefined),
  // De trendlaag (trend › ontwikkeling › bevinding, migratie 0052); alleen achter
  // de schakelaar ?trendlaag=1 in de nieuwe weergave.
  trendlaag: (sector = "ict") => get<TrendlaagResponse>("/trendlaag/trends", { sector }),
  trendlaagOntwikkeling: (id: string) =>
    get<TrendlaagOntwikkelingKaart>(`/trendlaag/ontwikkeling/${id}`),
  trendlaagStapel: (sector = "ict") => get<TrendlaagStapel>("/trendlaag/stapel", { sector }),
  trendlaagVan: (bevindingId: string) =>
    get<{ trendId: string | null; ontwikkelingId: string | null; rol: string }>(`/trendlaag/van/${bevindingId}`),
  trendlaagRust: (ids: readonly string[]) =>
    get<TrendlaagRust>("/trendlaag/rust", { ids: ids.join(",") }),
  trendlaagWaaromNu: (trendId: string) =>
    get<TrendlaagWaaromNu>(`/trendlaag/trend/${trendId}/waaromnu`),
  /** Eén bevinding als kaart, ook een waarom-nu-kandidaat die /trends/{id} niet toont. */
  trendlaagBevinding: (id: string) => get<TrendlaagBevindingKaart>(`/trendlaag/bevinding/${id}`),

  trends: (params?: TrendListParams) => get<TrendsListResponse>("/trends", params),
  trend: (id: string) => get<TrendDetailResponse>(`/trends/${id}`),
  trendsDelta: (periodA?: string, periodB?: string, sector?: string | null) =>
    get<TrendsDeltaResponse>("/trends/delta", { periodA, periodB, ...(sector ? { sector } : {}) }),

  skills: (params?: SkillListParams) => get<SkillsListResponse>("/skills", params),
  skill: (id: string) => get<SkillDetailResponse>(`/skills/${id}`),
  // De grafiekkaarten hangen aan het FILTER, niet aan de pagina — eigen route,
  // zodat bladeren geen drie extra queries kost.
  skillCharts: (params?: Omit<SkillListParams, "page" | "pageSize" | "sorteer" | "detail">) =>
    get<SkillChartsResponse>("/skills/charts", params),

  sources: (params?: SourceListParams, signal?: AbortSignal) => get<SourcesListResponse>("/sources", params, signal),
  source: (id: string) => get<SourceDetailResponse>(`/sources/${id}`),
  /**
   * Wat het rapport ZELF concludeert, plus de mini-inhoudsopgave van het
   * document. Aparte route en dus een tweede aanroep bij het openen van een
   * brondetailpaneel: deze lane raakt router.py niet aan, dat werd tijdens de
   * bouw door een parallelle sessie bewerkt.
   */
  bronConclusies: (id: string) => get<ConclusiesResponse>(`/sources/${id}/conclusies`),

  /**
   * Verkennen (scherm 15). De kaart komt in ÉÉN verzoek binnen en wordt
   * daarna client-side gefilterd: schuiven aan de tijdbalk of een filter mag
   * geen nieuw rondje kosten. `verwant` staat standaard niet in `soorten` —
   * dat zijn 48.405 van de 70.970 lijnen en het scherm heeft ze pas nodig als
   * de gebruiker breder wil kijken.
   */
  graafKaart: (params?: { sector?: string; soorten?: string }) =>
    get<GraafKaartResponse>("/graaf/kaart", params),
  graafBuurt: (knoop: string, params?: { soorten?: string; max_buren?: number }) =>
    get<GraafBuurtResponse>("/graaf/buurt", { knoop, ...(params ?? {}) }),
  /** Ronde 4 (03-09): waarom twee punten verbonden zijn, met de bronnen erachter. */
  graafVerband: (van: string, naar: string) =>
    get<GraafVerbandResponse>("/graaf/verband", { van, naar }),
  /** De leesbare alinea achter een citaat; niveau sectie of rapport voor meer. */
  graafAlinea: (id: string, niveau: "alinea" | "sectie" | "rapport" = "alinea") =>
    get<GraafAlineaResponse>(`/graaf/alinea/${encodeURIComponent(id)}`, { niveau }),
  /** De bronnen achter één punt (de bronnenring op laag 4). */
  graafBronnen: (punt: string) => get<GraafBronnenResponse>("/graaf/bronnen", { punt }),
  /** Zoeken op betekenis: 'soft skills' vindt de communicatievaardigheden. */
  graafZoek: (q: string, params?: { sector?: string; n?: number }) =>
    get<GraafZoekResponse>("/graaf/zoek", { q, ...(params ?? {}) }),
  /** Verkennen eenvoudig (editie 11): de rapporten achter de onderwerpen. */
  graafRapporten: (params?: { sector?: string }) =>
    get<GraafRapportenResponse>("/graaf/rapporten", params),
  /** Het kaartje van één skill: rapporten, uitgevers en de sterkste zin. */
  graafSkillkaart: (skill: string) => get<GraafSkillkaartResponse>("/graaf/skillkaart", { skill }),

  funding: () => get<FundingListResponse>("/funding"),
  /**
   * Eén regeling met haar bewijs én de as-labels (`assen`) — het endpoint
   * bestond al serverzijdig; deze helper kwam er pas toen de chat een
   * regeling ín het gesprek moest kunnen openen (Chat 2.0, besluit B5).
   */
  fundingDetail: (id: string) =>
    get<FundingDetailResponse>(`/funding/${encodeURIComponent(id)}`),

  /**
   * De Arbeidsmarkt-tab (W15/W16). Eigen lane in de leeslaag met een eigen
   * tabel: waarnemingen uit documenten naast de CBS-cijferreeksen.
   */
  arbeidsmarkt: (propositie?: string | null) =>
    get<ArbeidsmarktOverzicht>(
      "/arbeidsmarkt/overzicht",
      propositie ? { propositie } : undefined,
    ),
  observaties: (params?: ObservatieListParams) =>
    get<ObservatiesListResponse>("/arbeidsmarkt/observaties", params),
  observatie: (id: string, propositie?: string | null) =>
    get<ObservatieDetailResponse>(
      `/arbeidsmarkt/observaties/${id}`,
      propositie ? { propositie } : undefined,
    ),
  /** De hele tijdreeks achter één cijfer — elk getal is doorklikbaar. */
  reeks: (bron: string, indicator: string, opties?: { sleutel?: string; propositie?: string | null }) =>
    get<ArbeidsmarktReeksResponse>(
      `/arbeidsmarkt/reeks/${encodeURIComponent(bron)}/${encodeURIComponent(indicator)}`,
      opties?.sleutel || opties?.propositie
        ? {
            ...(opties.sleutel ? { sleutel: opties.sleutel } : {}),
            ...(opties.propositie ? { propositie: opties.propositie } : {}),
          }
        : undefined,
    ),

  /**
   * `type` is meervoudig (herhaalde parameter), net als de filters op de lijsten.
   * Zonder dat filter is de bak "nieuwe bron" onbereikbaar: de feed sorteert op
   * severity, en in ronde 1 dragen alle trends en skills de datum van de
   * nulmeting — die duwen de lichtere bak buiten elke limiet.
   */
  feed: (params?: {
    since?: string;
    limit?: number;
    type?: readonly string[];
    /** De sector van de workspace: zonder hem leest de funding-bak het hele
     *  register en krijgt een ICT-lezer subsidies voor hoveniers (26-08). */
    sector?: string;
  }) => get<FeedResponse>("/feed", params),

  /**
   * De gemeten feedposten (B6 golf 1, 27-08): versnellers, regionale
   * afwijking, cijfer van de maand, vooruitblik en opkomende termen —
   * berekend over bestaande data, $0 per ronde. Een lege lijst of null is
   * een meting, geen fout.
   */
  posten: (sector?: string | null) => get<PostenResponse>("/posten", sector ? { sector } : undefined),

  /**
   * Wie ben ik. Geeft 401 zolang de persoonlijke inlog niet bestaat (sessie 4)
   * en 403 als het adres niet is uitgenodigd — beide standen toont het scherm
   * met hun reden, want geen van beide is een fout van de gebruiker.
   */
  mij: () => get<Mij>("/mij"),

  /**
   * Het gebruikslog (W6, research §4): WELK scherm iemand opende, meer niet —
   * metadata, nooit inhoud. Vuur-en-vergeet: de aanroeper vangt de fout zelf
   * af, want een telling die hikt mag nooit een scherm laten haperen.
   */
  gebruik: (body: GebruikRequest) =>
    request<void>("/gebruik", { method: "POST", body: JSON.stringify(body) }),

  /**
   * De duim onder een eigen chatantwoord (W6). Alleen aan te roepen met een
   * `gesprekId` uit het antwoord: de server bewaart alleen gesprekken van een
   * ingelogde gebruiker, en een gesprek van iemand anders geeft 404.
   */
  duim: (gesprekId: string, body: DuimRequest) =>
    request<DuimResponse>(`/gesprekken/${encodeURIComponent(gesprekId)}/duim`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ── Beheer (W9). Alleen bereikbaar met de adminvlag; de service bewaakt dat,
  //    het scherm verbergt alleen de knoppen. ──────────────────────────────────

  /** De gebruikerslijst mét de twee keuzelijsten, zodat het scherm geen enum
   *  hoeft te kennen die in `config/` leeft. */
  gebruikers: () => get<GebruikersResponse>("/beheer/gebruikers"),

  uitnodigen: (body: UitnodigingRequest) =>
    request<Gebruiker>("/beheer/gebruikers", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** "Opnieuw sturen" (pilot 26-10): dezelfde uitnodigingsmail nog een keer.
   *  Het antwoord draagt `uitnodiging` (verstuurd, via, reden). */
  opnieuwSturen: (id: string) =>
    request<Gebruiker>(`/beheer/gebruikers/${id}/uitnodiging`, { method: "POST" }),

  /** Alleen de meegegeven velden wijzigen; een lege wijziging is geen fout. */
  wijzigGebruiker: (id: string, velden: Partial<UitnodigingRequest & { status: string }>) =>
    request<Gebruiker>(`/beheer/gebruikers/${id}`, {
      method: "PATCH",
      body: JSON.stringify(velden),
    }),

  wachtrij: () => get<WachtrijResponse>("/beheer/wachtrij"),

  /** Ja of nee op een verzoek. Bij een goedgekeurd kredietverzoek mag
   *  `nieuwMaximum` mee (W6): de server zet het in dezelfde transactie op de
   *  aanvrager, dus het scherm hoeft geen tweede aanroep te doen. */
  besluit: (verzoekId: string, body: BesluitRequest) =>
    request<Verzoek>(`/beheer/verzoeken/${verzoekId}/besluit`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * Een uitzondering afvinken vanaf de wachtrij (W6 ronde 2). Afvinken legt
   * vast dat de beheerder het zag; het verandert niets aan de inhoud.
   *
   * Review ronde 2: een eigen beheerroute in plaats van /review/inbox/{id}/ack.
   * Die oude route nam de naam van wie afvinkt uit de body aan — de browser
   * kon dus iedereen invullen. Hier bepaalt de SERVER wie het deed (de
   * ingelogde beheerder); de browser stuurt alleen een notitie. Een 404 zegt
   * in het Nederlands dat de uitzondering niet meer open staat.
   */
  afvinken: (id: string, body: AfvinkRequest) =>
    request<AfvinkResponse>(`/beheer/uitzonderingen/${encodeURIComponent(id)}/afvinken`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * Een afgeschermd document (achter een inlog of betaalmuur) toestaan of
   * afwijzen (owner 06-10, besluit K2). Toegestaan = het telt mee in de weging
   * vanaf de volgende herberekening; de citaten blijven in beide gevallen
   * zichtbaar. Het wachtrij-item gaat op de server dicht.
   */
  toegangBesluit: (documentId: string, body: ToegangRequest) =>
    request<ToegangResponse>(`/beheer/afgeschermd/${encodeURIComponent(documentId)}/besluit`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * Een verzoek indienen (W9; sinds W6 ook "meer krediet"). Een 409 betekent
   * dat hetzelfde verzoek al open staat — de server stuurt daar een
   * Nederlandse zin bij, die het scherm letterlijk toont.
   */
  dienVerzoekIn: (body: VerzoekRequest) =>
    request<Verzoek>("/verzoeken", { method: "POST", body: JSON.stringify(body) }),

  // ── De beheervlakken (W6 ronde 2). De server levert kant-en-klare zinnen,
  //    stoplichten en redenen; het scherm toont ze alleen. ────────────────────

  /** Zes kaarten en het maandverslag — het standaardvlak van Beheer. */
  overzicht: () => get<OverzichtResponse>("/beheer/overzicht"),

  /** Draait het? Runs per laan, wat aandacht vraagt, stille nul, pool. */
  gezondheid: () => get<GezondheidResponse>("/beheer/gezondheid"),

  /**
   * Het gezondheidsbord (B7): per propositie de vijf vakjes, de meters en de
   * voorwaarden uit de meterlijst. Lat, stand en woord komen van de server.
   */
  gezondheidsbord: (params?: { periode?: string; propositie?: string }) =>
    get<GezondheidsbordResponse>("/beheer/gezondheid/bord", params),

  /**
   * De wachters (pure-SQL-controles). Duurt op de echte database ~4 s, dus
   * ALLEEN aanroepen als de beheerder op "Controleer nu" klikt — nooit bij het
   * openen van het vlak, anders wacht elke bezoeker vier seconden op iets wat
   * hij misschien niet wil zien.
   */
  wachters: () => get<WachtersResponse>("/beheer/gezondheid/wachters"),

  // ── Bronnen & dekking (W6 ronde 3). Alles per sector; de server stuurt de
  //    aandelen, stoplichten, adviezen en de promotieregel al als zin. ────────

  /** Het corpus van één sector, plus de lijst met sectoren voor de keuze. */
  bronnen: (sector: string) => get<BronnenResponse>("/beheer/bronnen", { sector }),

  /** Vijf meters met doel (besluit D2), het raster categorie × belang en de
   *  categorieën buiten de vaste lijst. */
  dekking: (sector: string) => get<DekkingResponse>("/beheer/dekking", { sector }),

  /** Hoog · normaal · geblokkeerd met opbrengst en ruis, de kandidaten van
   *  buiten de lijst, de opschoonronde en de promotieregel als zin. */
  bronLijsten: (sector: string) =>
    get<BronLijstenResponse>("/beheer/bronnen/lijsten", { sector }),

  /** Wat blokkeren raakt, als één zin — het scherm toont hem VÓÓR de klik. */
  bronGevolg: (sector: string, domein: string) =>
    get<BronGevolgResponse>("/beheer/bronnen/gevolg", { sector, domein }),

  /**
   * Een besluit over een bron: hoog · normaal · geblokkeerd · opruimen ·
   * voorstel (een stem). Blokkeren en opruimen zonder motivering geven een 400
   * met een Nederlandse zin; die toont het scherm letterlijk. Opruimen wist
   * niets — het blokkeert en plant in (V4).
   */
  bronBesluit: (body: BronBesluitRequest) =>
    request<BronBesluitResponse>("/beheer/bronnen/besluit", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Een dekkingsgat als verzoek op de wachtrij. Een 409 betekent: staat al
   *  open — de server stuurt daar een zin bij. */
  dekkingVerzoek: (body: DekkingVerzoekRequest) =>
    request<DekkingVerzoekResponse>("/beheer/dekking/verzoek", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ── Kosten & credits (W6 ronde 4). De server stuurt elk bedrag, elk totaal,
  //    de stand en de zin van het plafond; het scherm rekent niets uit. ───────

  /** Per maand de drie soorten bedrag (vastgelegd · echt · berekend), het
   *  plafond, credits per persoon en waar de prijzen vandaan komen. */
  kosten: () => get<KostenResponse>("/beheer/kosten"),

  /**
   * Wat een run van `aantal` documenten in een laan ongeveer kost, met het
   * oordeel van de server (kostenmandaat: onder de go-grens mag hij door). Een
   * laan zonder prijs of zonder bruikbare runs is geen fout: 200 met oordeel
   * "onbekend" en een zin waarom. Een 422 draagt een Nederlandse zin die het
   * scherm letterlijk toont.
   */
  raming: (body: KostenRamingRequest) =>
    request<KostenRamingResponse>("/beheer/raming", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ── Kwaliteit (W6 ronde 5, research §3.6). De server stuurt de uitslag als
  //    zin, het verschil met de vorige benchmarkrun al uitgerekend en bij elke
  //    lege meter zijn reden; het scherm toont ze alleen. ─────────────────────

  /** Vier meters (steekproef, chat-benchmark, nog niet gemeten, funding) en de
   *  telling van de chatlogs. */
  kwaliteit: () => get<KwaliteitResponse>("/beheer/kwaliteit"),

  /** De 20 trends van de steekproef van deze maand, elk met zijn laatste toets. */
  steekproef: () => get<SteekproefResponse>("/beheer/steekproef"),

  /**
   * Eén toets (goed · twijfel · fout) op een trend uit de greep van deze maand.
   * Schrijft in de gedeelde database, dus alleen op een klik van de beheerder.
   * Een nieuw oordeel vervangt het vorige; niets wordt gewist. Een trend buiten
   * de greep geeft een 400 met een Nederlandse zin die het scherm letterlijk toont.
   */
  steekproefOordeel: (body: SteekproefOordeelRequest) =>
    request<SteekproefOordeelResponse>("/beheer/steekproef/oordeel", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** De chatlogs (V2: de inhoud alleen hier, twaalf maanden bewaard). */
  gesprekken: (params?: GesprekkenParams) =>
    get<GesprekkenResponse>("/beheer/gesprekken", params),

  /** Eén gesprek in zijn geheel. Na de bewaartermijn een 404 met een
   *  Nederlandse zin — die toont het scherm, niet een kaal "Niet gevonden." */
  gesprek: (id: string) => get<GesprekDetail>(`/beheer/gesprekken/${encodeURIComponent(id)}`),

  // ── De meterkast (Spoor B; endpoint uit sessie B1, scherm uit B2). Eén
  //    meterlijst, drie vergelijkingen: methode v1 tegen v2 (de benchmark),
  //    periode tegen vorige periode (het verloop), run tegen vorige run (wat
  //    deed die knop). De SERVER vormt stoplicht, verschil en reden; het scherm
  //    maakt er alleen getallen en woorden van. ──────────────────────────────

  /**
   * De vijf borden in één vergelijkstand. `methode` en `period` kiezen welke
   * kant "huidig" is; welke kant "basis" wordt, volgt uit `vergelijk`. Zonder
   * parameters: de nieuwste periode van methode v1, naast de vorige periode.
   */
  meterkast: (params?: {
    vergelijk?: MeterkastVergelijk;
    methode?: string;
    period?: string;
  }) => get<MeterkastResponse>("/beheer/meterkast", params),

  /**
   * "Bevries als ijkpunt": geef de metingen van een run een naam, zodat de
   * oude stand later nog terug te vinden is. Schrijft in de gedeelde database,
   * dus alleen op een klik. Idempotent; zonder runId de nieuwste automatische
   * run. Een 404 draagt een Nederlandse zin die het scherm letterlijk toont.
   */
  meterkastIjkpunt: (body: MeterkastIjkpuntRequest) =>
    request<MeterkastIjkpuntResponse>("/beheer/meterkast/ijkpunt", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * Bord 5: het oordeel van een mens (D1 opleidingsgrond, D2 editie-oordeel).
   * Alleen die twee meters; elke andere geeft een 400 met de zin van de server.
   * Schrijft in de gedeelde database, dus alleen op een klik.
   */
  meterkastOordeel: (body: MeterkastOordeelRequest) =>
    request<MeterkastOordeelResponse>("/beheer/meterkast/oordeel", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ── Het validatiedocument (VT-1). De accountmanager van een school en de
  //    beheerders vullen het in; elke klik is één POST, zodat stoppen en later
  //    verder gaan vanzelf werkt. De SERVER beslist wat zichtbaar is (vaste
  //    regels verdwijnen voor de AM) en telt de voortgang. ──────────────────────

  /** De drie blokken voor wie er kijkt, met de eigen antwoorden. 403 voor een
   *  AM van een andere school, 404 als de school (nog) geen document heeft. */
  validatie: (propositie: string) =>
    get<ValidatiePagina>(`/validatie/${encodeURIComponent(propositie)}`),

  /** De documenten die ik mag invullen, met mijn voortgang (release 06-10:
   *  één per pilotpropositie). Leeg voor wie er geen heeft, nooit 403. */
  validatieLijst: () => get<ValidatieLijstResponse>("/validatie"),

  // ── Het Plan (MBP fase 2, contract plan-v2). Eén gedeeld plan per
  //    propositie en jaar; ieder bewaart zijn eigen keuzes per paragraaf. ──

  /** De inhoud: de paragrafen en de lijst (nu voorbeeld). */
  plan: (propositie: string, jaar: number) =>
    get<PlanResponse>(`/plan/${encodeURIComponent(propositie)}/${jaar}`),

  /** De cijfers per vaardigheid voor de weergave van de lange lijst; alleen opgehaald als iemand ze nodig heeft. */
  planSkillcijfers: (propositie: string, jaar: number) =>
    get<SkillcijfersAntwoord>(`/plan/${encodeURIComponent(propositie)}/${jaar}/skillcijfers`),

  /** Wat ieder in dit plan koos, met de versie voor het volgende bewaren. */
  planStand: (propositie: string, jaar: number) =>
    get<StandResponse>(`/plan/${encodeURIComponent(propositie)}/${jaar}/stand`),

  /** Mijn stand in één paragraaf. Een oude versie geeft 409: eerst opnieuw ophalen. */
  bewaarPlanStand: (propositie: string, jaar: number, body: StandRequest) =>
    request<StandResponse>(`/plan/${encodeURIComponent(propositie)}/${jaar}/stand`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  /** Wat AM en BP samen hebben: de eigen volgorde van de lijst van 30 (baan 2P,
   *  pilot 26-10). Zelfde versieregel: een oude versie geeft 409. */
  bewaarPlanGedeeld: (propositie: string, jaar: number, body: GedeeldRequest) =>
    request<StandResponse>(`/plan/${encodeURIComponent(propositie)}/${jaar}/gedeeld`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  /** 2.3: een eigen pijn van de accountmanager, door Jev gekoppeld aan de pakketten van de lijst
   *  (één aanroep, geen Opus). Schrijft niets weg: de uitkomst gaat mee in de stand van 2.3. */
  planEigenPijn: (propositie: string, jaar: number, body: { tekst: string; belreden: string }) =>
    request<EigenPijnAntwoord>(`/plan/${encodeURIComponent(propositie)}/${jaar}/eigen-pijn`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Eén klik. Schrijft in de gedeelde database; het laatste antwoord telt. */
  validatieAntwoord: (propositie: string, body: ValidatieAntwoordRequest) =>
    request<ValidatieAntwoordResponse>(
      `/validatie/${encodeURIComponent(propositie)}/antwoord`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // ── Funding nakijken (K2, 07-10). Wat de rem bij de load en de nalezing niet
  //    zelf konden beslissen; per verschil kiest de beheerder. De SERVER sorteert,
  //    telt en controleert een eigen waarde per veldsoort. ──────────────────

  /** De controles; standaard de open. Een lege `risico`/`run` reist niet mee. */
  fundingControles: (params?: { besluit?: string; risico?: string; run?: string }) =>
    get<FundingControlesResponse>("/beheer/funding/controles", params),

  /** Eén besluit. Schrijft in de gedeelde database; laatste wint, niets wordt
   *  gewist. Een ongeldige eigen waarde geeft een 400 met een Nederlandse zin. */
  fundingControleBesluit: (id: string, body: FundingBesluitRequest) =>
    request<FundingControle>(`/beheer/funding/controles/${encodeURIComponent(id)}/besluit`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ── Funding vinden (fase 3): nieuwe vondsten en 'vervallen?'. ──────────────
  /** De twee secties; standaard de open besluiten. */
  fundingVondsten: (params?: { groep?: string }) =>
    get<FundingVondstenResponse>("/beheer/funding/vondsten", params),

  /** Eén besluit (opnemen · afwijzen met reden · klopt_vervallen · bestaat_nog).
   *  Schrijft in de gedeelde database; laatste wint, niets wordt gewist. */
  fundingVondstBesluit: (id: string, body: FundingVondstBesluitRequest) =>
    request<FundingVondst>(`/beheer/funding/vondsten/${encodeURIComponent(id)}/besluit`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Het overzicht voor de owner: per regel wie wat zei. Alleen beheer. */
  validatieOverzicht: () => get<ValidatieOverzichtResponse>("/beheer/validatie"),

  /** Beheer → Plannen (baan 2P): per pilotpropositie AM en BP naast elkaar, wie
   *  nog niet begon en de laatste 30 wijzigingen. Alleen beheer. */
  beheerPlannen: () => get<PlannenResponse>("/beheer/plannen"),

  /** Beheer, Plannen (blok 8): per plan wie invult, de personen en de stand. Alleen beheer. */
  planInstellingen: () => get<PlanInstellingenResponse>("/beheer/plannen/instellingen"),

  /** "Start plan": legt vast wie het plan invult. Na de start staat het vast (409). */
  startPlan: (propositie: string, jaar: number, body: PlanStartRequest) =>
    request<PlanInstelling>(`/beheer/plannen/${encodeURIComponent(propositie)}/${jaar}/start`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Het deelvenster (blok 8): vaste versies en wie nu kan meelezen. */
  planDelen: (propositie: string, jaar: number) =>
    get<PlanDelenResponse>(`/plan/${encodeURIComponent(propositie)}/${jaar}/delen`),

  voegPlanMeelezerToe: (propositie: string, jaar: number, email: string) =>
    request<PlanDelenResponse>(`/plan/${encodeURIComponent(propositie)}/${jaar}/delen/meelezers`, {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  trekPlanMeelezerIn: (propositie: string, jaar: number, gebruiker: string) =>
    request<PlanDelenResponse>(
      `/plan/${encodeURIComponent(propositie)}/${jaar}/delen/meelezers/${encodeURIComponent(gebruiker)}`,
      { method: "DELETE" },
    ),

  /** Een vaste versie maken: de stand van dit moment, die niet meer verandert. */
  maakPlanVersie: (propositie: string, jaar: number) =>
    request<PlanVersieMeta>(`/plan/${encodeURIComponent(propositie)}/${jaar}/delen/versie`, {
      method: "POST",
    }),

  /** Een vaste versie lezen (ook voor wie alleen meeleest). */
  planVersie: (propositie: string, jaar: number, id: string) =>
    get<PlanVersieAntwoord>(
      `/plan/${encodeURIComponent(propositie)}/${jaar}/versie/${encodeURIComponent(id)}`,
    ),

  /** Beheer → Ketenbalans (sessie 0b): per propositie of er per stap iets stil
   *  verdwijnt, en de poort vóór zichtbaar maken. Alleen beheer; de server oordeelt. */
  ketenbalans: () => get<KetenbalansResponse>("/beheer/ketenbalans"),

  /** Een rode moet-0-meter bewust toelaten, met reden. Schrijft in de gedeelde
   *  database (ketenbalans_uitzonderingen), dus alleen op een klik. */
  ketenbalansUitzondering: (body: KetenbalansUitzonderingRequest) =>
    request<KetenbalansUitzonderingResponse>("/beheer/ketenbalans/uitzondering", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  ketenbalansIntrekken: (id: number) =>
    request<KetenbalansUitzonderingResponse>(
      `/beheer/ketenbalans/uitzondering/${id}/intrekken`,
      { method: "POST" },
    ),

  /** De poort: alleen melden (standaard) of afdwingen. Alleen op een klik. */
  ketenbalansSchakelaar: (body: KetenbalansSchakelaarRequest) =>
    request<KetenbalansSchakelaarResponse>("/beheer/ketenbalans/schakelaar", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Wist ALLEEN de keuzes van testaccounts en wat beheer via "Bekijk als"
   *  schreef. Schrijft in de gedeelde database, dus alleen op een klik. */
  testkeuzesWissen: (propositie: string, jaar: number) =>
    request<PlannenWissenResponse>(
      `/beheer/plannen/${encodeURIComponent(propositie)}/${jaar}/testkeuzes-wissen`,
      { method: "POST" },
    ),

  /** Een regel vast zetten (de AM ziet hem dan niet meer), of terughalen. */
  validatieVast: (propositie: string, body: ValidatieVastRequest) =>
    request<ValidatieVastResponse>(
      `/beheer/validatie/${encodeURIComponent(propositie)}/vast`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // ── Configuratie (W6 ronde 6, research §3.7). De onderwerpen met hun
  //    tellingen, de zoekwoorden met hun opbrengst en de instellingen (V5). De
  //    server controleert elke waarde; het scherm stuurt alleen wat er staat. ──

  /** Onderwerpen, zoekopdrachten en instellingen in één antwoord. */
  configuratie: () => get<ConfiguratieResponse>("/beheer/configuratie"),

  /**
   * Voorstellen uit de betekenis (V6): de trends zonder onderwerp van één
   * sector, gegroepeerd op hun embedding. Een dry-run zonder naam en zonder
   * "live zetten". Duurt op de echte data ~5 s, dus ALLEEN op een klik op
   * "Maak voorstellen" — nooit bij het openen van het vlak.
   */
  topicvoorstellen: (sector: string) =>
    get<TopicvoorstellenResponse>("/beheer/topicvoorstellen", { sector }),

  /**
   * Een instelling vastleggen of terugzetten naar de standaard (V5). Schrijft
   * in de gedeelde database (alleen toevoegen: de laatste rij wint), dus alleen
   * op een klik. Een 400 draagt een Nederlandse zin ("Kies een getal van 0 tot
   * en met 100.") die het scherm letterlijk toont.
   */
  instelling: (body: InstellingRequest) =>
    request<Instelling>("/beheer/instellingen", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * Baan C2 (ownerantwoord 13): per sector welke UWV-beroepen erbij horen — de
   * best guess naast de aanpassing van een beheerder. Alleen bij het openen van
   * het tabblad Beroepen.
   */
  beroepen: () => get<BeroepenResponse>("/beheer/beroepen"),

  /**
   * De volledige lijst beroepen voor één sector vastleggen, of terug naar de
   * best guess. Schrijft in de gedeelde database (alleen toevoegen), dus alleen
   * op een klik; een 400 draagt een zin die het scherm letterlijk toont.
   */
  beroepenOpslaan: (body: BeroepenRequest) =>
    request<SectorBeroepen>("/beheer/beroepen", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ── Gebruikers & engagement (W6 ronde 7, research §3.8). Alleen tellingen en
  //    tijden per persoon (AVG, V2: nooit wat iemand vroeg); de server stuurt
  //    het totaal, de tijdschatting in woorden en bij een lege rij de reden. ──

  /** Iedereen met zijn gebruik in de laatste 7, 30 of 90 dagen en zijn krediet.
   *  Een andere periode geeft een 400 met een Nederlandse zin. */
  engagement: (dagen: number) => get<EngagementResponse>("/beheer/engagement", { dagen }),

  /**
   * Het persoonlijke maximum van één kredietsoort zetten, of weghalen met
   * `maximum: null` (dan geldt weer de standaard van de rol). Schrijft in de
   * gedeelde database, dus alleen op een klik. Een 400 (onbekende soort) en een
   * 404 (onbekende gebruiker) dragen een Nederlandse zin die het scherm
   * letterlijk toont; het antwoord draagt de zin die het scherm meldt.
   */
  kredietZetten: (id: string, body: KredietRequest) =>
    request<KredietResponse>(`/beheer/gebruikers/${encodeURIComponent(id)}/krediet`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  inbox: (params?: { status?: string; kind?: string }) =>
    get<InboxResponse>("/review/inbox", params),
  sample: (params?: { n?: number; seed?: string }) =>
    get<SampleResponse>("/review/sample", params),

  chat: (body: ChatRequest) =>
    request<ChatResponse>("/chat", { method: "POST", body: JSON.stringify(body) }),

  /**
   * Zelfde antwoord als `chat`, maar de toolstappen komen live binnen via
   * `onStap` — zo ziet de gebruiker wát de chat nakijkt in plaats van een
   * blinde spinner (artifact §5: "toon het bonnetje, niet de gedachten").
   *
   * GEEN EventSource: die kan alleen GET en zet geen headers, terwijl de vraag
   * met een body mee moet. Dus `fetch` + de reader van de body.
   *
   * TERUGVAL IS INGEBOUWD. Draait er een oudere server, of buffert er iets in
   * de keten waardoor de stroom niet aankomt, dan valt hij terug op de gewone
   * POST. Het antwoord is dan hetzelfde, alleen zonder statusregel — een
   * chat die stukloopt op een cosmetische laag zou de verkeerde prijs zijn.
   */
  chatStream: async (
    body: ChatRequest,
    onStap?: (stap: ChatStap) => void,
  ): Promise<ChatResponse> => {
    let res: globalThis.Response;
    try {
      res = await fetch("/api/chat/stream", {
        method: "POST",
        headers: {
          Accept: "text/event-stream",
          "Content-Type": "application/json",
          // Deze route gaat bewust buiten `request` om (SSE heeft de reader
          // nodig), dus het token moet hier apart mee — anders is de chat het
          // enige scherm dat uitgelogd raakt.
          ...(await authKop()),
          ...trendlaagKop(),
          ...bekijkAlsKoppen(),
        },
        body: JSON.stringify(body),
      });
    } catch {
      return api.chat(body);
    }
    if (!res.ok || !res.body) {
      if (res.status === 404 || res.status === 405) return api.chat(body);
      // Een echte fout (503 zonder modelsleutel, 401) hoort dezelfde melding
      // te geven als de gewone route, dus laten we `request` hem opnieuw doen.
      return api.chat(body);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let antwoord: ChatResponse | null = null;
    let fout: string | null = null;

    const verwerk = (blok: string) => {
      let soort = "";
      const regels: string[] = [];
      for (const regel of blok.split("\n")) {
        if (regel.startsWith("event: ")) soort = regel.slice(7).trim();
        else if (regel.startsWith("data: ")) regels.push(regel.slice(6));
      }
      if (!regels.length) return;
      let data: unknown;
      try {
        data = JSON.parse(regels.join("\n"));
      } catch {
        return; // een halve regel is geen gebeurtenis; wachten op de volgende
      }
      if (soort === "stap") onStap?.(data as ChatStap);
      else if (soort === "klaar") antwoord = data as ChatResponse;
      else if (soort === "fout") fout = (data as { detail?: string }).detail ?? null;
    };

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let grens = buffer.indexOf("\n\n");
      while (grens !== -1) {
        verwerk(buffer.slice(0, grens));
        buffer = buffer.slice(grens + 2);
        grens = buffer.indexOf("\n\n");
      }
    }
    if (buffer.trim()) verwerk(buffer);

    if (fout) throw new ApiError(502, "/chat/stream", fout);
    if (antwoord) return antwoord;
    // Stroom viel stil zonder slotgebeurtenis: dan liever het antwoord alsnog
    // ophalen dan een leeg bericht tonen.
    return api.chat(body);
  },

  /**
   * De openingsvragen per rol. Faalt hij, dan toont de chat zijn eigen
   * terugval — hij is een handleiding, geen voorwaarde om te kunnen vragen.
   */
  chatOpeningsvragen: () => get<ChatOpeningsvragen>("/chat/openingsvragen"),

  /**
   * De keuzelijst "binnen welke propositie" boven de chat (backlog 38) en de
   * standaard van de ingelogde gebruiker (zijn profiel). Faalt hij, dan
   * verstuurt de chat geen propositie en kiest de server zelf (profiel of
   * hele organisatie).
   */
  chatProposities: () => get<ChatProposities>("/chat/proposities"),
};

// ── De trendlaag (sof/readapi/trendlaag.py) ──────────────────────────────────

export type TrendlaagOntwikkeling = {
  id: string;
  titel: string;
  werkzin: string | null;
  soort: "regel" | "werkvloer" | "markt" | "onderwijs" | null;
  datum: string | null;
  opkomend: boolean;
  bronnen: number | null;
  eerste: string | null;
  laatste: string | null;
  regelMetDatum: boolean;
  /** Alleen in /trendlaag/stapel: de vaardigheden van de bevindingen (brug). */
  skills?: { id: string; expliciet: boolean }[];
};
export type TrendlaagTrend = {
  id: string;
  plek: number;
  onderwerp: string;
  titel: string;
  subtekst: string;
  waaromNu: string | null;
  soortTrend: "vak" | "arbeidsmarkt";
  opkomend: boolean;
  bronnen: number | null;
  bronSoorten: Record<string, number>;
  nOntwikkelingen: number | null;
  nBevindingen: number | null;
  beweging: number | null;
  bewegingLabel: string | null;
  urgentie: number | null;
  score: number | null;
  scores: Record<string, number | null>;
  vlaggen: string[];
  ontwikkelingen: TrendlaagOntwikkeling[];
};
export type TrendlaagResponse = {
  sector: string;
  kerngetallen: {
    trends: number;
    ontwikkelingen: number;
    bevindingen: number;
    ingedeeld: number | null;
    nietIngedeeld: number;
    perSoort: Record<string, number>;
    regelMetDatum: number;
    opkomend: number;
  };
  trends: TrendlaagTrend[];
};
export type TrendlaagOntwikkelingKaart = TrendlaagOntwikkeling & {
  soortKaart: "ontwikkeling";
  trendId: string | null;
  trendTitel: string | null;
  onderwerp: string | null;
  bevindingen: { id: string; titel: string; tldr: string | null; publisher: string | null; datum: string | null; quote: string | null }[];
};
export type TrendlaagStapel = {
  sector: string;
  onderwerpen: { onderwerp: string; trends: number; ontwikkelingen: number; bronnen: number }[];
  trends: TrendlaagTrend[];
  regelsMetDatum: (TrendlaagOntwikkeling & { trendId: string; trendTitel: string; onderwerp: string })[];
};
/** Het trendpaneel: de Waarom nu-regels (W1, getal vooraan). */
export type TrendlaagWaaromNu = {
  totaal: number;
  regels: { id: string; titel: string | null; getal: string | null; publisher: string | null; datum: string | null }[];
};

/** De kaart van één bevinding (GET /trendlaag/bevinding/{id}): titel, één zin
 *  (tldr, anders de claim van het jongste signaal), haar plek in de trendlaag
 *  en de bronnen met citaat, jongste eerst. */
export type TrendlaagBevindingKaart = {
  soortKaart: "bevinding";
  id: string;
  titel: string | null;
  zin: string | null;
  peildatum: string | null;
  rol: "lid" | "los" | "waarom_nu" | "niet_ingedeeld" | null;
  trendId: string | null;
  trendTitel: string | null;
  onderwerp: string | null;
  ontwikkelingId: string | null;
  ontwikkelingTitel: string | null;
  bronnen: { titel: string | null; publisher: string | null; datum: string | null; quote: string | null; url: string | null }[];
};

export type TrendlaagRust = {
  trends: {
    trendId: string;
    titel: string;
    onderwerp: string;
    bronnen: number | null;
    waaromNu: string | null;
    ontwikkelingen: { id: string; titel: string; werkzin: string | null }[];
    bevindingen: string[];
    nummers: number[];
  }[];
  nietIngedeeld: string[];
};
